/**
 * Audio Element Player
 */

import * as React from 'react';
import {
    cn,
    Card,
    CardContent,
    AudioPlayer as AudioPlayerControl,
} from '@ai-content-hub/content-kit';
import { useElementUITranslations } from '../i18n/elementUI';
import type { ElementPlayerProps } from '@ai-content-hub/content-kit';
import { resolveMediaUrlForViewer, shouldResolveMediaUrlForViewer } from '@ai-content-hub/content-core';
import { Loader2 } from 'lucide-react';
import type { AudioElementData } from './schema';

export interface AudioPlayerProps extends ElementPlayerProps<AudioElementData> {}

export const AudioPlayer: React.FC<AudioPlayerProps> = ({ data, className }) => {
    const et = useElementUITranslations();
    const rawSrc = (data?.src ?? '').trim();

    const [resolvedSrc, setResolvedSrc] = React.useState<string | null>(() =>
        rawSrc && !shouldResolveMediaUrlForViewer(rawSrc) ? rawSrc : null,
    );
    const [resolving, setResolving] = React.useState(() =>
        Boolean(rawSrc && shouldResolveMediaUrlForViewer(rawSrc)),
    );

    React.useEffect(() => {
        const s = (data?.src ?? '').trim();
        if (!s || typeof window === 'undefined') {
            setResolvedSrc(null);
            setResolving(false);
            return;
        }
        if (!shouldResolveMediaUrlForViewer(s)) {
            setResolvedSrc(s);
            setResolving(false);
            return;
        }
        let cancelled = false;
        setResolving(true);
        setResolvedSrc(null);
        void (async () => {
            try {
                const uri = await resolveMediaUrlForViewer(s);
                if (!cancelled) {
                    setResolvedSrc(uri || s);
                }
            } catch {
                if (!cancelled) {
                    setResolvedSrc(s);
                }
            } finally {
                if (!cancelled) {
                    setResolving(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [data?.src]);

    // Validate data exists
    if (!data) {
        return (
            <Card className={cn('audio-element-placeholder', className)}>
                <CardContent className="py-8 text-center text-muted-foreground">
                    No audio data
                </CardContent>
            </Card>
        );
    }

    const {
        src,
        title,
        autoplay = false,
        loop = false,
        showControls = true,
    } = data;

    if (!src) {
        return (
            <Card className={cn('audio-element-placeholder', className)}>
                <CardContent className="py-8 text-center text-muted-foreground">
                    No audio selected
                </CardContent>
            </Card>
        );
    }

    const displaySrc = resolvedSrc ?? '';
    const showProtectedLoading =
        Boolean(rawSrc && shouldResolveMediaUrlForViewer(rawSrc)) && (resolving || !resolvedSrc);

    // Pause on unmount so playback stops when navigating away (TC011).
    // Do not removeAttribute('src')+load(): React Strict Mode remount keeps the
    // same src prop and skips re-applying the DOM attribute → dead <audio>
    // with no source (import CDN URLs look fine in JSON but won't play).
    const audioRef = React.useRef<HTMLAudioElement | null>(null);
    React.useEffect(() => {
        return () => {
            audioRef.current?.pause();
        };
    }, []);

    return (
        <Card className={cn('audio-element', className)}>
            <CardContent className="py-2">
                {title && <h4 className="text-sm font-medium mb-2">{title}</h4>}
                {showProtectedLoading ? (
                    <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                        <Loader2 className="h-5 w-5 animate-spin shrink-0" />
                        <span>{et.loadingAudio}</span>
                    </div>
                ) : showControls ? (
                    <AudioPlayerControl
                        src={displaySrc}
                        autoPlay={autoplay}
                        loop={loop}
                        className="audio-element-player"
                    />
                ) : (
                    <audio
                        ref={audioRef}
                        src={displaySrc}
                        autoPlay={autoplay}
                        loop={loop}
                        className="hidden"
                    >
                        Your browser does not support the audio element.
                    </audio>
                )}
            </CardContent>
        </Card>
    );
};

export default AudioPlayer;
